/* eslint-disable no-console */
import type { EventName, EventPropertiesMap } from '../types/events';
import type { UserTraits, UserContext } from '../types/user';
import type {
  AnalyticsContext,
  ApiValue,
  GroupTraits
} from '../types/properties';

export interface AnalyticsConfig {
  writeKey: string;
  dataPlaneUrl: string;
  enabled: boolean;
  debug?: boolean;
}

export abstract class BaseAnalyticsClient {
  protected config: AnalyticsConfig;
  protected userContext?: UserContext;
  protected anonymousId?: string;
  protected groupContext?: { groupId: string };

  constructor(config: AnalyticsConfig) {
    this.config = config;
  }

  // Logging helpers with [Analytics] prefix
  protected log(message: string, data?: unknown): void {
    if (data !== undefined) {
      console.log(`[Analytics] ${message}`, data);
    } else {
      console.log(`[Analytics] ${message}`);
    }
  }

  protected warn(message: string, data?: unknown): void {
    if (data !== undefined) {
      console.warn(`[Analytics] ${message}`, data);
    } else {
      console.warn(`[Analytics] ${message}`);
    }
  }

  protected error(message: string, data?: unknown): void {
    if (data !== undefined) {
      console.error(`[Analytics] ${message}`, data);
    } else {
      console.error(`[Analytics] ${message}`);
    }
  }

  // initialize analytics client

  abstract initialize(): Promise<void>;

  // track event using type-safe properties
  trackEvent<T extends EventName>(
    eventName: T,
    properties: EventPropertiesMap[T],
    options?: {
      context?: AnalyticsContext;
      userId?: string;
      anonymousId?: string;
    }
  ): void {
    if (!this.config.enabled) {
      if (this.config.debug) {
        this.log(`Tracking disabled: ${eventName}`, properties);
      }
      return;
    }

    // Use provided userId or fall back to userContext (set via identify/setUserContext)
    const effectiveUserId = options?.userId || this.userContext?.userId;

    const effectiveAnonymousId = options?.anonymousId || this.anonymousId;

    if (!effectiveUserId && !effectiveAnonymousId) {
      this.warn(
        `Track: ${eventName} - Neither userId nor anonymousId provided. ` +
          'RudderStack requires at least one identifier. Event may be rejected.'
      );
    }

    if (this.config.debug) {
      this.log(`Track: ${eventName}`, {
        ...properties,
        userId: effectiveUserId,
        anonymousId: effectiveAnonymousId
      });
    }

    this._track(
      eventName,
      properties as Record<string, ApiValue>,
      options?.context,
      effectiveUserId,
      effectiveAnonymousId
    );
  }
  // internal track method
  protected abstract _track(
    eventName: string,
    properties: Record<string, ApiValue>,
    context?: AnalyticsContext,
    userId?: string,
    anonymousId?: string
  ): void;

  identifyUser(
    userId: string,
    traits?: UserTraits,
    options?: {
      context?: AnalyticsContext;
      anonymousId?: string;
    }
  ): void {
    if (!this.config.enabled) {
      if (this.config.debug) {
        this.log(`Identify disabled: ${userId}`, traits);
      }
      return;
    }

    if (this.config.debug) {
      this.log(`Identify: ${userId}`, {
        traits,
        anonymousId: options?.anonymousId
      });
    }

    // update user context (userId fallback for subsequent track calls)
    this.userContext = { userId };

    this._identify(userId, traits, options?.context, options?.anonymousId);
  }

  protected abstract _identify(
    userId: string,
    traits?: UserTraits,
    context?: AnalyticsContext,
    anonymousId?: string
  ): void;

  // associate the current user with a group (organization, account, company)
  // note: Node SDK requires userId for server-side group calls;
  // uses provided userId or falls back to userContext (set via identify/setUserContext)
  groupUser(
    groupId: string,
    traits?: GroupTraits,
    options?: {
      context?: AnalyticsContext;
      userId?: string;
      anonymousId?: string;
    }
  ): void {
    if (!this.config.enabled) {
      if (this.config.debug) {
        this.log(`Group disabled: ${groupId}`, traits);
      }
      return;
    }

    const effectiveUserId = options?.userId || this.userContext?.userId;

    const effectiveAnonymousId = options?.anonymousId || this.anonymousId;

    if (!effectiveUserId && !effectiveAnonymousId) {
      this.warn(
        `Group: ${groupId} - Neither userId nor anonymousId available. ` +
          'RudderStack Node SDK requires userId for group calls.'
      );
    }

    if (this.config.debug) {
      this.log(`Group: ${groupId}`, {
        traits,
        userId: effectiveUserId,
        anonymousId: effectiveAnonymousId
      });
    }

    this.groupContext = { groupId };

    this._group(
      groupId,
      traits,
      options?.context,
      effectiveUserId,
      effectiveAnonymousId
    );
  }

  protected abstract _group(
    groupId: string,
    traits?: GroupTraits,
    context?: AnalyticsContext,
    userId?: string,
    anonymousId?: string
  ): void;

  // set user context (alternative to identify, lightweight)
  setUserContext(context: UserContext): void {
    this.userContext = context;
  }

  // clear user context (e.g. user logout)
  clearUserContext(): void {
    this.userContext = undefined;
  }

  // anonymize - clear user identity without full reset (preserves anonymousId)
  protected _anonymize(): void {
    // Default no-op; override in SDK-specific clients as needed
  }

  anonymize(): void {
    this.clearUserContext();
    this._anonymize();
  }

  setAnonymousId(anonymousId: string): void {
    this.anonymousId = anonymousId;
  }

  getAnonymousId(): string | undefined {
    return this.anonymousId;
  }

  clearAnonymousId(): void {
    this.anonymousId = undefined;
  }

  setGroupContext(groupId: string): void {
    this.groupContext = { groupId };
  }

  getGroupId(): string | undefined {
    return this.groupContext?.groupId;
  }

  clearGroupContext(): void {
    this.groupContext = undefined;
  }

  // reset - clear all user data
  abstract reset(): void;
}
